import React from 'react';
import {SelectChangeEvent} from '@mui/material';
import {CheckInFormValues} from '../util/check-in-field-validation';
import type {CheckInFormSubmissionValues} from '../../../hooks/checkin/useCheckInSubmit/interfaces';

/**
 * Props for the CheckInForm component.
 * Controls form visibility, callbacks, and loading state.
 */
export interface CheckInFormProps {
  onFormSave: (formValues: CheckInFormSubmissionValues) => void | Promise<void>;
  onCancel: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  loading: boolean;
}

/**
 * Internal state shape for all form fields.
 * All fields are strings to match input element values.
 */
export interface FormState {
  containerNumber: string;
  chassisNumber: string;
  chassisLicensePlateNumber: string;
  sealNumber: string;
  trailerNumber: string;
  driverFirstName: string;
  driverLastName: string;
  driverLicenseNumber: string;
  truckLicensePlateNumber: string;
  scacCode: string;
  assetSize: string;
  notes: string;
}

/**
 * Validation error messages by field name.
 * All properties are optional—missing keys indicate no error for that field.
 */
export interface FormErrors {
  containerNumber?: string;
  chassisNumber?: string;
  sealNumber?: string;
  trailerNumber?: string;
  driverFirstName?: string;
  driverLastName?: string;
  driverLicenseNumber?: string;
  truckLicensePlateNumber?: string;
  assetSize?: string;
  scacCode?: string;
  submit?: string;
}

/**
 * Default empty form state with all fields initialized to empty strings.
 */
export const EMPTY_FORM: FormState = {
  containerNumber: '',
  chassisNumber: '',
  chassisLicensePlateNumber: '',
  sealNumber: '',
  trailerNumber: '',
  driverFirstName: '',
  driverLastName: '',
  driverLicenseNumber: '',
  truckLicensePlateNumber: '',
  scacCode: '',
  assetSize: '',
  notes: '',
};

/**
 * Shared base props for all reusable check-in field components.
 * Each field component calls `onValueChange(fieldName, transformedValue)` after
 * applying its own input transformations (uppercase, space-stripping, etc.).
 */
export interface CheckInFieldProps {
  value: string;
  onValueChange: (name: string, value: string) => void;
  error?: string;
  required?: boolean;
}

/**
 * Props passed from the CheckInForm orchestrator to each per-asset-type form component.
 * The form component renders its own field layout using the shared field components.
 */
export interface AssetTypeFormProps {
  form: FormState;
  errors: FormErrors;
  onValueChange: (name: string, value: string) => void;
  onAssetSizeChange: (event: SelectChangeEvent) => void;
}

/**
 * Configuration entry for the form registry.
 * Maps an asset type to its form component and co-located validation function.
 */
export interface FormConfig {
  component: React.ComponentType<AssetTypeFormProps>;
  validate: (values: CheckInFormValues) => string | undefined;
}
